import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Users, UsersRound, Tag, Clock, CheckCircle2, XCircle, ExternalLink } from "lucide-react";

function StatCard({ label, value, icon: Icon, testId, tone = "navy" }) {
  const bg = {
    navy: "#e6ecf7",
    green: "#cdf5e1",
    yellow: "#fff4c2",
    red: "#ffd6d1",
  }[tone];
  const fg = {
    navy: "#1a2340",
    green: "#005c46",
    yellow: "#8a6c00",
    red: "#9b1300",
  }[tone];
  return (
    <div className="b2b-card p-5" data-testid={testId}>
      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ background: bg, color: fg }}
        >
          <Icon size={18} />
        </div>
        <div className="text-sm" style={{ color: "var(--brand-muted)" }}>
          {label}
        </div>
      </div>
      <div className="text-3xl font-semibold" style={{ color: "var(--brand-navy)" }}>
        {value ?? 0}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        subtitle="Overview of your wholesale B2B operations"
        actions={
          <Link
            to="/register"
            target="_blank"
            className="btn-secondary inline-flex items-center gap-2"
            data-testid="dashboard-view-registration"
          >
            <ExternalLink size={14} /> Registration Form
          </Link>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total Customers" value={stats?.customers?.total} icon={Users} tone="navy" testId="stat-total" />
        <StatCard label="Pending" value={stats?.customers?.pending} icon={Clock} tone="yellow" testId="stat-pending" />
        <StatCard label="Approved" value={stats?.customers?.approved} icon={CheckCircle2} tone="green" testId="stat-approved" />
        <StatCard label="Rejected" value={stats?.customers?.rejected} icon={XCircle} tone="red" testId="stat-rejected" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatCard label="Customer Groups" value={stats?.groups} icon={UsersRound} tone="navy" testId="stat-groups" />
        <StatCard label="Pricing Rules" value={stats?.pricing_rules} icon={Tag} tone="navy" testId="stat-pricing-rules" />
      </div>

      <div className="b2b-card p-6 mt-8" data-testid="dashboard-getting-started">
        <h3 className="text-lg font-semibold mb-2" style={{ color: "var(--brand-navy)" }}>
          Getting started with V-VENTURE
        </h3>
        <p className="text-sm mb-4" style={{ color: "var(--brand-muted)" }}>
          Complete these steps to launch your wholesale program.
        </p>
        <ol className="text-sm space-y-2 list-decimal ml-5" style={{ color: "var(--brand-text)" }}>
          <li>Connect your Shopify store from the <Link to="/settings" className="underline">Settings</Link> page.</li>
          <li>Share your <Link to="/register" className="underline">public registration form</Link> with wholesale buyers.</li>
          <li>Review pending applications and approve wholesalers from <Link to="/customers" className="underline">Customers</Link>.</li>
          <li>Set up <Link to="/pricing-rules" className="underline">pricing rules</Link> and <Link to="/order-limits" className="underline">order limits</Link>.</li>
        </ol>
      </div>
    </div>
  );
}
